import React, { createContext, useContext, useState, useEffect } from "react";
import { Level } from "../data/LevelModel";

interface GameStateContextType {
  currentLevel: Level | null;
  setCurrentLevel: (level: Level | null) => void;
  score: number;
  setScore: (score: number) => void;
  adultMode: boolean;
  setAdultMode: (mode: boolean) => void;
  statsLevel: number | null;
  setStatsLevel: (level: number | null) => void;
  completedLevels: string[];
  setCompletedLevels: (levels: string[]) => void;
  markLevelCompleted: (levelNickname: string) => void;
}

const GameStateContext = createContext<GameStateContextType | undefined>(
  undefined,
);

export const GameStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentLevel, setCurrentLevel] = useState<Level | null>(null);
  const [score, setScore] = useState(0);
  const [adultMode, setAdultMode] = useState(true);
  const [statsLevel, setStatsLevel] = useState<number | null>(null);
  const [completedLevels, setCompletedLevels] = useState<string[]>([]);

  // Load completed levels from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("completedLevels");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setCompletedLevels(parsed);
        }
      } catch (error) {
        console.warn(
          "Failed to parse completed levels from localStorage:",
          error,
        );
      }
    }
  }, []);

  // Save completed levels to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("completedLevels", JSON.stringify(completedLevels));
  }, [completedLevels]);

  const markLevelCompleted = (levelNickname: string) => {
    if (!completedLevels.includes(levelNickname)) {
      setCompletedLevels((prev) => [...prev, levelNickname]);
    }
  };

  return (
    <GameStateContext.Provider
      value={{
        currentLevel,
        setCurrentLevel,
        score,
        setScore,
        adultMode,
        setAdultMode,
        statsLevel,
        setStatsLevel,
        completedLevels,
        setCompletedLevels,
        markLevelCompleted,
      }}
    >
      {children}
    </GameStateContext.Provider>
  );
};

export const useGameState = () => {
  const context = useContext(GameStateContext);
  if (context === undefined) {
    throw new Error("useGameState must be used within a GameStateProvider");
  }
  return context;
};
